import { FLAGS } from '@iheartjane/feature-flags';
import { Typography } from '@iheartjane/reefer';
import type { ComplianceLanguageSettings, Nullable } from '@iheartjane/types';
import { clsx } from 'clsx';
import capitalize from 'lodash-es/capitalize';
import { useEffect, useRef, useState } from 'react';

import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { StoreSpecial } from '@jane/bloom/viewModels/schemas/storeSpecials';

import styles from './dealsBanner.module.css';

type Accumulator = {
  bulkSpecials: StoreSpecial[];
  cartTotalSpecials: StoreSpecial[];
  groupSpecials: StoreSpecial[];
};

export const DealsBanner = ({
  complianceLanguage,
  storeSpecials,
}: {
  complianceLanguage?: Nullable<ComplianceLanguageSettings>;
  storeSpecials: StoreSpecial[];
}) => {
  const { featureFlags } = useRootLoaderData();
  const extendedDealsLabelsEnabled = featureFlags[FLAGS.c1ExtendedDealsLabels];

  const { bulkSpecials, cartTotalSpecials, groupSpecials } =
    storeSpecials.reduce<Accumulator>(
      (acc, special) => {
        if (special.special_type === 'cart_total') {
          acc.cartTotalSpecials.push(special);
        } else if (special.special_type === 'bulk_pricing') {
          acc.bulkSpecials.push(special);
        } else if (special.special_type === 'qualified_group') {
          acc.groupSpecials.push(special);
        }
        return acc;
      },
      { bulkSpecials: [], cartTotalSpecials: [], groupSpecials: [] }
    );

  const contentRef = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const container = contentRef.current;

    const handleScroll = () => {
      if (container) {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const containerWidth = container.offsetWidth;
        const marginOfError = 10;

        const isAtEnd =
          scrollLeft + containerWidth >= scrollWidth - marginOfError;

        setAtEnd(isAtEnd);
      }
    };

    container?.addEventListener('scroll', handleScroll);

    // Initial check for scrollability on component mount to handle edge cases
    handleScroll();

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles['bannerContainer']}>
      <div
        className={clsx(styles['bannerContent'], {
          [styles['fadeRight']]: !atEnd,
          [styles['noFade']]: atEnd,
        })}
        ref={contentRef}
      >
        {cartTotalSpecials.length > 0 && (
          <>
            <Typography variant="body-semibold" color="inherit">
              {extendedDealsLabelsEnabled
                ? capitalize(complianceLanguage?.['cart_total'] || '') ||
                  'Cart total'
                : 'Cart total'}
            </Typography>
            {cartTotalSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
        {bulkSpecials.length > 0 && (
          <>
            <Typography variant="body-semibold" color="inherit">
              {extendedDealsLabelsEnabled
                ? capitalize(complianceLanguage?.['bulk_pricing'] || '') ||
                  'Bulk'
                : 'Bulk'}
            </Typography>
            {bulkSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
        {groupSpecials.length > 0 && (
          <>
            <Typography variant="body-semibold" color="inherit">
              Group discount
            </Typography>
            {groupSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
